import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight, Plus } from 'lucide-react';
import { Navbar } from '../Navbar';
import Footer from '../Footer';
import { DashboardGuide } from '../DashboardGuide';
import { DashboardSkeleton } from '../SkeletonLoader';
import { DashboardErrorState } from '../ErrorState';
import { OnboardingDetector } from '../OnboardingDetector';
import { TrialBanner } from '../TrialBanner';
import { QuickActionsSection } from './QuickActionsSection';
import { RepositoryListSection } from './RepositoryListSection';
import { useDashboardData } from './useDashboardData';
import { useDashboardGuidance } from './useDashboardGuidance';
import type { User } from '../../contexts/JWTAuthContext';

interface DashboardProps {
  user?: User | null;
  isFirstVisit?: boolean;
}

export const Dashboard: React.FC<DashboardProps> = ({ user: propUser, isFirstVisit = false }) => {
  const navigate = useNavigate();

  const {
    isDemoMode,
    flowType,
    subscriptionStatus,
    loading,
    error,
    dashboardData,
    setDashboardData,
    repositories,
    totalCount,
    statusCounts,
    totalDashboardPages,
    currentPage,
    setCurrentPage,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    filterStatus,
    setFilterStatus,
    refreshDashboardData,
    ITEMS_PER_PAGE,
  } = useDashboardData(propUser, isFirstVisit);

  const {
    handleGuidanceNeeded,
    handleGuidanceNotNeeded,
    handleGuidanceStepChange,
    handleGuidanceComplete,
    handleGuidanceDismiss,
  } = useDashboardGuidance(setDashboardData);

  const handleRetryLoad = () => {
    refreshDashboardData();
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <DashboardSkeleton />
      </>
    );
  }

  if (error && !dashboardData.repositories.length) {
    return (
      <>
        <Navbar />
        <DashboardErrorState error={error} onRetry={handleRetryLoad} />
      </>
    );
  }

  const hasRepositories = statusCounts.all > 0;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Navbar />

      <OnboardingDetector
        user={dashboardData.user}
        repositories={dashboardData.repositories}
        analyses={dashboardData.recentAnalyses || []}
        onGuidanceNeeded={handleGuidanceNeeded}
        onGuidanceNotNeeded={handleGuidanceNotNeeded}
        enabled={!loading && !error}
      />

      <DashboardGuide
        isActive={dashboardData.guidance.enabled}
        currentStep={dashboardData.guidance.currentStep}
        onStepChange={handleGuidanceStepChange}
        onComplete={handleGuidanceComplete}
        onDismiss={handleGuidanceDismiss}
        showProgress={true}
        allowSkip={true}
        animationsEnabled={true}
        steps={[
          {
            id: 'welcome',
            target: '[data-guide="dashboard-header"]',
            title: 'Welcome to your dashboard!',
            content:
              'This is your home base where you can see all your projects and get help improving your code.',
            position: 'auto',
            animation: 'glow',
            nextAction: 'manual',
            estimatedDuration: 30,
          },
          {
            id: 'quick-actions',
            target: '[data-guide="quick-actions"]',
            title: 'Quick Actions',
            content:
              'These buttons help you get started. Connect your code from GitHub, upload a file, or see your past results.',
            position: 'auto',
            animation: 'pulse',
            nextAction: 'manual',
            estimatedDuration: 45,
            actionButton: {
              text: 'Try Connect Project',
              action: () => navigate('/repositories'),
              variant: 'secondary',
              icon: <Plus className="w-3 h-3" />,
            },
          },
          {
            id: 'repositories',
            target: '[data-guide="repositories"]',
            title: 'Your Projects',
            content:
              "Your connected projects show up here. You can see if they're healthy and get new suggestions.",
            position: 'auto',
            animation: 'glow',
            nextAction: 'manual',
            estimatedDuration: 40,
          },
          {
            id: 'complete',
            target: '[data-guide="dashboard-header"]',
            title: "You're all set!",
            content:
              'Take your time exploring. If you need help again, just look for the help button in the top navigation.',
            position: 'auto',
            animation: 'bounce',
            nextAction: 'manual',
            estimatedDuration: 20,
            actionButton: {
              text: 'Start Using Dashboard',
              action: () => {
                const firstAction = document.querySelector(
                  '[data-guide="quick-actions"] button',
                ) as HTMLElement;
                if (firstAction) {
                  firstAction.focus();
                }
              },
              variant: 'primary',
              icon: <ArrowRight className="w-3 h-3" />,
            },
          },
        ]}
      />

      <main className="pt-24 px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          {!isDemoMode &&
            subscriptionStatus?.isTrial &&
            subscriptionStatus.trialDaysRemaining !== null && (
              <TrialBanner
                daysRemaining={subscriptionStatus.trialDaysRemaining}
                planName={subscriptionStatus.plan?.name || 'your'}
              />
            )}

          {/* Header */}
          <div className="mb-8 flex items-start justify-between" data-guide="dashboard-header">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl text-gray-900 dark:text-white">
                  Welcome back, <span className="font-bold">{dashboardData.user.name}</span>
                </h1>
                {(isDemoMode || flowType === 'demo') && (
                  <span className="px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
                    Demo Mode
                  </span>
                )}
              </div>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {isDemoMode || flowType === 'demo'
                  ? "This is a demonstration of Autar.ai's capabilities with sample data."
                  : hasRepositories
                    ? `${statusCounts.issues + statusCounts.error} projects require immediate attention.`
                    : "Let's get started by connecting your first project."}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {error && (
                <div className="flex items-center gap-1 text-sm text-orange-600 dark:text-orange-400">
                  <AlertCircle className="w-4 h-4" />
                  <span className="hidden sm:inline">Some data may be outdated</span>
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-8">
            <QuickActionsSection />

            <RepositoryListSection
              repositories={repositories}
              totalCount={totalCount}
              statusCounts={statusCounts}
              filterStatus={filterStatus}
              setFilterStatus={setFilterStatus}
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalDashboardPages={totalDashboardPages}
              ITEMS_PER_PAGE={ITEMS_PER_PAGE}
            />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
